import { inject, type InjectionKey, type MaybeRefOrGetter, provide, type Ref, toValue } from 'vue'
import { useEventListener } from '@vueuse/core'
import { useFocusTrap, type UseFocusTrapOptions } from '@vueuse/integrations/useFocusTrap'

import { useOverlayStack } from './useOverlayStack'

const OVERLAY_ROOTS = '.app-modal, .app-drawer'
const TABBABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]'
export interface ParentFocusTrap {
  pause: () => void
  resume: () => void
  wrapFocus: (backward: boolean) => boolean
}

const PARENT_TRAP_KEY: InjectionKey<ParentFocusTrap> = Symbol('overlayFocusTrap')

export function useOverlayFocusTrap(
  wrapperRef: Ref<HTMLElement | null | undefined>,
  isOpen: MaybeRefOrGetter<boolean>,
  options?: UseFocusTrapOptions,
) {
  const { activate, deactivate, pause, unpause } = useFocusTrap(wrapperRef, {
    ...options,
    immediate: false,
  })

  const { isTop: isTopOverlay, stackStyle } = useOverlayStack(isOpen)

  const getTabbables = () => Array.from(wrapperRef.value?.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR) || [])
    .filter(el => el.tabIndex >= 0 && !(el as HTMLButtonElement).disabled && (el as HTMLInputElement).type !== 'hidden' &&
      el.getClientRects().length)

  const wrapFocus = (backward: boolean): boolean => {
    const tabbables = getTabbables()
    const edge = backward ? tabbables[0] : tabbables.at(-1)
    if (!edge || document.activeElement !== edge) return false
    ;(backward ? tabbables.at(-1) : tabbables[0])?.focus()
    return true
  }

  const resume = () => {
    if (wrapperRef.value?.contains(document.activeElement)) unpause()
  }

  provide(PARENT_TRAP_KEY, { pause, resume, wrapFocus })

  useEventListener(document, 'focusin', (event) => {
    if (!toValue(isOpen)) return
    const target = event.target
    if (!(target instanceof Element)) return
    if (target.closest('.app-select-items')) return
    const targetRoot = target.closest(OVERLAY_ROOTS)
    if (targetRoot && targetRoot !== wrapperRef.value?.closest(OVERLAY_ROOTS)) return
    unpause()
  })

  return { activate, deactivate, isTopOverlay, stackStyle }
}

export function useParentFocusTrap(): ParentFocusTrap {
  return inject(PARENT_TRAP_KEY, { pause: () => {}, resume: () => {}, wrapFocus: () => false })
}
